// World Bank в интерфейсе: каталог показателей и регистрация выбранного.

import type { WorldBankCatalogItem, WorldBankCatalogSearch, WorldBankCatalogStatus } from '../types';
import { api } from '../api';
import { formatDateTime, formatNumber as formatLocalizedNumber } from '../i18n';

import { controls } from './controls';
import { currentLocale } from './locale';
import { errorMessage, setStatus } from './status';
import { escapeHtml } from './dom';
import { loadIndicatorSections, populateIndicators, renderLeftWorldBankSelected } from './indicators';
import { state } from './state';
import { updateIndicatorControls } from './widget-form';

export function worldBankCatalogUserSummary(status: WorldBankCatalogStatus | null): string {
  if (!status || status.total_count === 0) {
    return 'Каталог ещё не загружен.';
  }
  const fetchedAt = status.fetched_at
    ? formatDateTime(status.fetched_at, currentLocale())
    : 'дата неизвестна';
  const stale = status.stale ? ' · можно обновить' : '';
  return `${formatLocalizedNumber(status.total_count, currentLocale(), 0)} показателей · обновлено ${fetchedAt}${stale}`;
}

export function renderWorldBankCatalogStatus(status: WorldBankCatalogStatus | null): void {
  state.worldBankCatalogStatus = status;
  controls.leftWorldBankCatalogStatus.textContent = worldBankCatalogUserSummary(status);
}

export function renderWorldBankCatalogResults(result: WorldBankCatalogSearch): void {
  state.worldBankCatalogResults = result.items;
  renderWorldBankCatalogStatus(result.catalog);
  if (!result.items.length) {
    controls.leftWorldBankCatalogResults.innerHTML = '<p>Ничего не найдено. Попробуйте другое название или код показателя.</p>';
    return;
  }
  controls.leftWorldBankCatalogResults.innerHTML = `
    <div class="emiss-catalog-result-meta">Показано ${formatLocalizedNumber(result.items.length, currentLocale(), 0)} из ${formatLocalizedNumber(result.total, currentLocale(), 0)}</div>
    ${result.items.map((item) => `
      <button class="emiss-catalog-result left-emiss-catalog-result" type="button" data-left-worldbank-catalog-select="${escapeHtml(item.indicator_id)}" title="${escapeHtml(`Международная статистика ${item.indicator_id}`)}">
        <span class="emiss-catalog-result-title">${escapeHtml(item.name)}</span>
        <span class="emiss-catalog-result-details">
          <span class="emiss-catalog-code">${escapeHtml(item.indicator_id)}</span>
          ${item.registered ? '<span class="emiss-catalog-badge registered">уже подключён</span>' : ''}
          <span class="emiss-catalog-action">${item.registered ? 'Выбрать для виджета' : 'Подключить показатель'}</span>
        </span>
      </button>
    `).join('')}
  `;
}

export function setWorldBankCatalogBusy(busy: boolean): void {
  controls.leftWorldBankCatalogSearch.disabled = busy;
  controls.leftWorldBankCatalogRefresh.disabled = busy;
}

export async function refreshWorldBankCatalog(showMessage = true): Promise<WorldBankCatalogStatus> {
  setWorldBankCatalogBusy(true);
  if (showMessage) {
    controls.leftWorldBankCatalogResults.innerHTML = '<p>Обновляю каталог международной статистики…</p>';
  }
  try {
    const result = await api.refreshWorldBankCatalog();
    renderWorldBankCatalogStatus(result);
    if (showMessage) {
      controls.leftWorldBankCatalogResults.innerHTML = '<p>Каталог обновлён. Введите название или код показателя для поиска.</p>';
      setStatus(`Каталог международной статистики обновлён: ${formatLocalizedNumber(result.total_count, currentLocale(), 0)} показателей`, 'success');
    }
    return result;
  } catch (error) {
    if (showMessage) {
      controls.leftWorldBankCatalogResults.innerHTML = `<p class="error-text">${escapeHtml(errorMessage(error))}</p>`;
      setStatus(`Каталог международной статистики не обновлён: ${errorMessage(error)}`, 'error');
    }
    throw error;
  } finally {
    setWorldBankCatalogBusy(false);
  }
}

export async function refreshWorldBankCatalogStatus(): Promise<void> {
  try {
    renderWorldBankCatalogStatus(await api.worldBankCatalogStatus());
  } catch {
    state.worldBankCatalogStatus = null;
    controls.leftWorldBankCatalogStatus.textContent = 'Статус каталога недоступен.';
  }
}

export async function ensureWorldBankCatalogForSearch(): Promise<void> {
  if (state.worldBankCatalogStatus === null) {
    renderWorldBankCatalogStatus(await api.worldBankCatalogStatus());
  }
  if ((state.worldBankCatalogStatus?.total_count ?? 0) > 0) return;
  controls.leftWorldBankCatalogStatus.textContent = 'Каталог пуст, загружаю список международных показателей...';
  await refreshWorldBankCatalog(false);
}

export async function searchWorldBankCatalog(): Promise<void> {
  const query = controls.indicatorSearch.value.trim() || controls.leftWorldBankCatalogQuery.value.trim();
  const sectionId = controls.indicatorSection.value;
  setWorldBankCatalogBusy(true);
  controls.leftWorldBankCatalogResults.innerHTML = '<p>Ищу в каталоге международной статистики...</p>';
  try {
    await ensureWorldBankCatalogForSearch();
    const result = await api.searchWorldBankCatalog(query, 25, 0, currentLocale(), sectionId);
    renderWorldBankCatalogResults(result);
    setStatus(
      result.total
        ? `В каталоге международной статистики найдено ${formatLocalizedNumber(result.total, currentLocale(), 0)} показателей`
        : 'В каталоге международной статистики ничего не найдено',
      result.total ? 'success' : 'normal',
    );
  } catch (error) {
    controls.leftWorldBankCatalogResults.innerHTML = `<p class="error-text">${escapeHtml(errorMessage(error))}</p>`;
    setStatus(`Поиск в каталоге международной статистики не выполнен: ${errorMessage(error)}`, 'error');
  } finally {
    setWorldBankCatalogBusy(false);
  }
}

export async function selectWorldBankCatalogItem(item: WorldBankCatalogItem): Promise<void> {
  controls.source.value = 'worldbank';
  await loadIndicatorSections();
  populateIndicators();
  if (item.registered && item.local_indicator_id) {
    if (!state.indicators.some((indicator) => indicator.id === item.local_indicator_id)) {
      state.indicators = await api.indicators(true, currentLocale());
      populateIndicators();
    }
    controls.indicator.value = item.local_indicator_id;
    renderLeftWorldBankSelected();
    await updateIndicatorControls();
    setStatus(`Международный показатель выбран: ${item.name}`, 'success');
    return;
  }
  setWorldBankCatalogBusy(true);
  controls.leftWorldBankCatalogResults.insertAdjacentHTML('afterbegin', '<p>Подключаю международный показатель…</p>');
  try {
    const registered = await api.registerWorldBank(item.indicator_id);
    state.indicators = [
      ...state.indicators.filter((indicator) => indicator.id !== registered.indicator.id),
      registered.indicator,
    ];
    await loadIndicatorSections();
    populateIndicators();
    controls.indicator.value = registered.indicator.id;
    renderLeftWorldBankSelected();
    await updateIndicatorControls(undefined, undefined, undefined, {}, []);
    setStatus(`Международный показатель подключён: ${registered.indicator.name}`, 'success');
  } catch (error) {
    setStatus(`Не удалось подключить международный показатель: ${errorMessage(error)}`, 'error');
  } finally {
    setWorldBankCatalogBusy(false);
  }
}
